import { BriefcaseBusiness, ChartColumn, PieChart, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { usePeriod, useProfitabilityReport, type ProfitabilityReport } from '../api'
import { TrendChart } from '../components/Charts'
import { BarList, KpiGrid, ReportCard, type TableSpec } from '../components/ReportBits'
import { ReportLayout } from '../components/ReportLayout'
import type { Kpi } from '../types'
import { fmtValue } from '../visuals'

const money = (v: number) => fmtValue(v, 'money')
const pct = (v: number | null) => (v === null ? '—' : fmtValue(v, 'pct'))
const kpi = (key: string, value: number, format: Kpi['format'] = 'money', goodWhenUp = true): Kpi => ({
  key,
  value,
  format,
  deltaPct: null,
  goodWhenUp,
  spark: [],
})

/** Proje ve müşteri bazında gelir, gider (satış süreci giderleri dahil) ve kâr; gelir/gider kaynakları. Tutarlar KDV hariçtir. */
export function ProfitabilityReportPage() {
  const { t } = useTranslation()
  const { months } = usePeriod()
  const q = useProfitabilityReport(months)
  const r = q.data
  const [view, setView] = useState<'projects' | 'customers'>('projects')

  const projectSpec = (x: ProfitabilityReport): TableSpec => ({
    head: [
      t('reports.col.project'),
      t('reports.col.contract'),
      t('reports.profit.income'),
      t('reports.profit.expense'),
      t('reports.profit.salesExpense'),
      t('reports.profit.profit'),
      t('reports.profit.margin'),
    ],
    rows: x.projects.map((p) => [
      p.name,
      money(p.contractValue),
      money(p.income),
      money(p.expense),
      money(p.salesExpense),
      money(p.profit),
      pct(p.margin),
    ]),
    align: ['left', 'right', 'right', 'right', 'right', 'right', 'right'],
  })
  const customerSpec = (x: ProfitabilityReport): TableSpec => ({
    head: [
      t('reports.col.customer'),
      t('reports.profit.orders'),
      t('reports.profit.invoiced'),
      t('reports.profit.collected'),
      t('reports.profit.outstanding'),
      t('reports.profit.salesExpense'),
      t('reports.profit.profit'),
    ],
    rows: x.customers.map((c) => [
      c.title,
      money(c.ordersTotal),
      money(c.invoiced),
      money(c.collected),
      money(c.outstanding),
      money(c.salesExpense),
      money(c.profit),
    ]),
    align: ['left', 'right', 'right', 'right', 'right', 'right', 'right'],
  })

  return (
    <ReportLayout
      name="karlilik"
      title={t('reports.pages.profitability.title')}
      description={t('reports.pages.profitability.subtitle')}
      query={q}
      sheets={
        r
          ? () => [
              { title: t('reports.profit.projects'), spec: projectSpec(r) },
              { title: t('reports.profit.customers'), spec: customerSpec(r) },
            ]
          : undefined
      }
    >
      {r ? (
        <>
          <KpiGrid
            kpis={[
              kpi('income', r.totals.income),
              kpi('cost', r.totals.expense + r.totals.salesExpense, 'money', false),
              kpi('profit', r.totals.profit),
              kpi('orders', r.totals.ordersTotal),
            ]}
            label={(k) => t(`reports.profit.kpi.${k}`)}
            hint={(k) =>
              k === 'profit'
                ? t('reports.profit.marginHint', { margin: pct(r.totals.margin) })
                : k === 'orders'
                  ? t('reports.profit.ordersHint', {
                      collected: money(r.totals.collected),
                      outstanding: money(r.totals.outstanding),
                    })
                  : k === 'cost'
                    ? t('reports.profit.costHint', { sales: money(r.totals.salesExpense) })
                    : t('reports.profit.exVat')
            }
          />

          <ReportCard
            icon={ChartColumn}
            title={t('reports.profit.monthly')}
            description={t('reports.profit.monthlyHint')}
            legend={[
              { label: t('reports.profit.income'), color: 'var(--series-1)' },
              { label: t('reports.profit.expense'), color: 'var(--series-2)' },
              { label: t('reports.col.net'), color: 'var(--series-3)' },
            ]}
            table={{
              head: [
                t('reports.col.month'),
                t('reports.profit.income'),
                t('reports.profit.expense'),
                t('reports.col.net'),
              ],
              rows: r.months.map((m) => [m.month, money(m.income), money(m.expense), money(m.net)]),
              align: ['left', 'right', 'right', 'right'],
            }}
          >
            <TrendChart
              data={r.months}
              format="money"
              zeroLine
              series={[
                { key: 'income', label: t('reports.profit.income'), kind: 'bar' },
                { key: 'expense', label: t('reports.profit.expense'), kind: 'bar' },
                { key: 'net', label: t('reports.col.net'), kind: 'line' },
              ]}
            />
          </ReportCard>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {(['income', 'expense'] as const).map((kind) => (
              <ReportCard
                key={kind}
                icon={PieChart}
                title={t(`reports.profit.${kind}Sources`)}
                description={t('reports.profit.sourcesHint')}
              >
                <BarList
                  format="money"
                  items={r.categories
                    .filter((c) => c.kind === kind)
                    .map((c) => ({
                      key: c.id || c.name,
                      label: c.name,
                      value: c.amount,
                      sub: t('reports.profit.records', { count: c.count }),
                    }))}
                />
              </ReportCard>
            ))}
          </div>

          <ReportCard
            icon={view === 'projects' ? BriefcaseBusiness : Users}
            title={t(view === 'projects' ? 'reports.profit.projects' : 'reports.profit.customers')}
            description={t(view === 'projects' ? 'reports.profit.projectsHint' : 'reports.profit.customersHint')}
            actions={
              <Tabs value={view} onValueChange={(v) => setView(v as typeof view)}>
                <TabsList size="sm">
                  <TabsTrigger value="projects">{t('reports.profit.projects')}</TabsTrigger>
                  <TabsTrigger value="customers">{t('reports.profit.customers')}</TabsTrigger>
                </TabsList>
              </Tabs>
            }
          >
            {view === 'projects' ? <ProjectTable r={r} /> : <CustomerTable r={r} />}
          </ReportCard>
        </>
      ) : null}
    </ReportLayout>
  )
}

const tone = (v: number) => (v < 0 ? 'text-destructive' : v > 0 ? 'text-success' : '')

function ProjectTable({ r }: { r: ProfitabilityReport }) {
  const { t } = useTranslation()
  if (!r.projects.length) return <p className="text-muted-foreground py-8 text-center text-sm">{t('reports.noData')}</p>
  return (
    <div className="overflow-x-auto">
      <Table>
        <THead>
          <TR>
            <TH>{t('reports.col.project')}</TH>
            <TH className="text-right">{t('reports.col.contract')}</TH>
            <TH className="text-right">{t('reports.profit.income')}</TH>
            <TH className="text-right">{t('reports.profit.expense')}</TH>
            <TH className="text-right">{t('reports.profit.salesExpense')}</TH>
            <TH className="text-right">{t('reports.profit.profit')}</TH>
            <TH className="text-right">{t('reports.profit.margin')}</TH>
            <TH className="text-right">{t('reports.profit.budgetUsed')}</TH>
          </TR>
        </THead>
        <TBody>
          {r.projects.map((p) => (
            <TR key={p.id}>
              <TD>
                <span className="font-medium">{p.name}</span>
                {p.customers.length ? (
                  <span className="text-muted-foreground block text-xs">{p.customers.join(', ')}</span>
                ) : null}
              </TD>
              <TD className="text-right tabular-nums">{money(p.contractValue)}</TD>
              <TD className="text-right tabular-nums">{money(p.income)}</TD>
              <TD className="text-right tabular-nums">{money(p.expense)}</TD>
              <TD className="text-right tabular-nums">{money(p.salesExpense)}</TD>
              <TD className={cn('text-right font-semibold tabular-nums', tone(p.profit))}>{money(p.profit)}</TD>
              <TD className="text-right tabular-nums">{pct(p.margin)}</TD>
              <TD className={cn('text-right tabular-nums', (p.budgetUsed ?? 0) > 100 && 'text-destructive')}>
                {pct(p.budgetUsed)}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  )
}

function CustomerTable({ r }: { r: ProfitabilityReport }) {
  const { t } = useTranslation()
  if (!r.customers.length)
    return <p className="text-muted-foreground py-8 text-center text-sm">{t('reports.noData')}</p>
  return (
    <div className="overflow-x-auto">
      <Table>
        <THead>
          <TR>
            <TH>{t('reports.col.customer')}</TH>
            <TH className="text-right">{t('reports.profit.quotes')}</TH>
            <TH className="text-right">{t('reports.profit.orders')}</TH>
            <TH className="text-right">{t('reports.profit.invoiced')}</TH>
            <TH className="text-right">{t('reports.profit.collected')}</TH>
            <TH className="text-right">{t('reports.profit.outstanding')}</TH>
            <TH className="text-right">{t('reports.profit.salesExpense')}</TH>
            <TH className="text-right">{t('reports.profit.profit')}</TH>
          </TR>
        </THead>
        <TBody>
          {r.customers.map((c) => (
            <TR key={c.id}>
              <TD className="font-medium">{c.title}</TD>
              <TD className="text-right tabular-nums">
                {money(c.quotesTotal)}
                <span className="text-muted-foreground block text-xs">
                  {t('reports.profit.count', { count: c.quotesCount })}
                </span>
              </TD>
              <TD className="text-right tabular-nums">
                {money(c.ordersTotal)}
                <span className="text-muted-foreground block text-xs">
                  {t('reports.profit.count', { count: c.ordersCount })}
                </span>
              </TD>
              <TD className="text-right tabular-nums">{money(c.invoiced)}</TD>
              <TD className="text-right tabular-nums">{money(c.collected)}</TD>
              <TD className={cn('text-right tabular-nums', c.outstanding > 0 && 'text-warning-strong')}>
                {money(c.outstanding)}
              </TD>
              <TD className="text-right tabular-nums">{money(c.salesExpense)}</TD>
              <TD className={cn('text-right font-semibold tabular-nums', tone(c.profit))}>{money(c.profit)}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  )
}
